import { useTranslation } from 'next-i18next';
import {
  ArrowPathIcon, PaperAirplaneIcon, SparklesIcon, VideoCameraIcon,
} from '@heroicons/react/24/solid';
import { CardServiceProps } from '@components/features/cards/services/ServiceCard';
import { useMemo } from 'react';


// Interface
// *****************************************************
interface TalentPremiumBenefitsProps {
  className?: string,
}

/**
 * @description Premium benefits section
 * * **className** - Optional class names
 */
export const TalentPremiumBenefits = (props: TalentPremiumBenefitsProps) => {
  // Destructure props
  const { className = '' } = props;

  // Translation
  const { t } = useTranslation('common');

  // Cards list data
  // *******************************
  const cardsList = useMemo(() => {
    const list: CardServiceProps[] = [
      {
        title: t('premium.talent.benefits.auto_apply.heading'),
        description: t('premium.talent.benefits.auto_apply.description'),
        icon: <ArrowPathIcon className="size-6 text-secondary" />,
      },
      {
        title: t('premium.talent.benefits.unlimited_live.heading'),
        description: t('premium.talent.benefits.unlimited_live.description'),
        icon: <VideoCameraIcon className="size-6 text-secondary" />,
      },
      {
        title: t('premium.talent.benefits.direct_messages.heading'),
        description: t('premium.talent.benefits.direct_messages.description'),
        icon: <PaperAirplaneIcon className="size-6 text-secondary" />,
      },
      {
        title: t('premium.talent.benefits.profile_highlight.heading'),
        description: t('premium.talent.benefits.profile_highlight.description'),
        icon: <SparklesIcon className="size-6 text-secondary" />,
      },
    ];

    return list;
  }, [t]);


  // Render component
  // *****************************************************
  return (
    <div className={`${className}`}>
      <div className="text-left md:text-center">
        <h1 className="mb-4 text-4xl font-extrabold text-dark">{t('premium.talent.benefits.title')}</h1>
        <p>
          {t('premium.talent.benefits.subtitle')}
        </p>
      </div>

      {/* Benefits list */}
      <div className="mt-10 grid gap-3 text-left md:grid-cols-2">
        {cardsList.map((card, index) => (
          <div key={`item-${index + 1}`} className="flex justify-between rounded-md bg-surface-50 p-5">
            <div className="mr-3 flex items-center">
              <div className="rounded-full bg-surface p-3">
                {card.icon}
              </div>
            </div>
            <div>
              <div className="md:flex md:justify-between">
                {card.badgeText && (
                  <div className="mb-3 md:order-last md:mb-0 md:ml-3">
                    <span className="text-nowrap rounded-md bg-dark px-2 py-1 text-xs font-bold text-white">
                      {card.badgeText}
                    </span>
                  </div>
                )}
                <h2 className="mb-3 text-lg font-bold leading-tight">{card.title}</h2>
              </div>
              <p className="leading-snug">{card.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
